import 'react-app-polyfill/ie11';
import 'react-app-polyfill/stable';
import React, { Suspense, useEffect, useMemo } from 'react';
import ReactDOM from 'react-dom';
import { Provider, useSelector } from 'react-redux';
import Jaws from '@oms/jaws';
import { Link } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from 'react-query';
import { OMSContext, defaultConfig } from '@oms/components-context';
import { theme } from 'styles/theme';

import { BASE_URL, SECURE_BASE_URL } from 'constants/Jaws';

import Routes from './routes';
import configureStore from './store/configureStore';

import { startKeepAlive } from 'components/KeepAlive';
import { selectIsUserLoggedIn } from './state/user/selectors';
import useConfigureJaws from 'utils/configureJaws';
import { jawsLog, jawsRegister } from 'utils/jawsDebug';

// Uncomment this if you want to use MSW
// if (process.env.NODE_ENV === 'development') {
//   const { worker } = require('./mocks/browser');
//   worker.start();
// }

startKeepAlive();

export const store = configureStore();
const { dispatch } = store;

const useWebSockets =
  (localStorage.getItem('streamingEnabled') || 'true') === 'true';

const componentsContext = {
  ...defaultConfig, // Includes defaults
  Link, // Overrides Link
  transactionsUrl:
    '{+baseUrl}/secure/transactions/{backend}/{userId}/{portfolioId}{/transactionId}{?instrumentId,fromDate,settledStatus,query*}',
};

if (process.env.NODE_ENV === 'development') {
  require('debug').enable('dev');
}
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
    },
  },
});
const elem = document.getElementById('root');
const JawsContext = () => {
  const isLoggedIn = useSelector(selectIsUserLoggedIn);
  const jawsContext = useMemo(() => {
    return {
      instance: jawsRegister(
        'index',
        new Jaws({
          url: isLoggedIn ? SECURE_BASE_URL : BASE_URL,
          useWebSockets,
        }),
      ),
    };
  }, [isLoggedIn]);

  // useMemo tworzy nowa instancje przy zmianie isLoggedIn (anonimowy BASE_URL -> SECURE_BASE_URL
  // po zalogowaniu), ale starej nikt nie rozlaczal. Jej gniazdo WebSocket zostawalo otwarte do
  // konca zycia strony - w Network widac je jako polaczenie, przez ktore nic nie leci.
  useEffect(() => {
    const { instance } = jawsContext;

    // Po dluzszym uspieniu karty przegladarka zamyka to gniazdo, a ta instancja - w
    // odroznieniu od tej z configureJaws - nie ma ZADNEGO wznawiania. Widzety strumieniowe
    // (Vinnere, Tapere, Tungvektere...) zostawaly wtedy na spinnerze az do F5, mimo ze
    // serwer byl calkiem zdrowy. Potwierdzone pomiarem: po powrocie karty stan tej instancji
    // to 'disconnected', a reczne wywolanie reconnect() z konsoli natychmiast przywracalo dane.
    const revive = () => {
      if (document.visibilityState !== 'visible') return;
      if (
        instance &&
        instance.state === 'disconnected' &&
        typeof instance.reconnect === 'function'
      ) {
        jawsLog('index', 'karta wrocila, instancja martwa - wznawiam');
        instance.reconnect();
      }
    };

    document.addEventListener('visibilitychange', revive);
    window.addEventListener('online', revive);

    return () => {
      document.removeEventListener('visibilitychange', revive);
      window.removeEventListener('online', revive);
      if (instance && typeof instance.disconnect === 'function') {
        instance.disconnect();
      }
    };
  }, [jawsContext]);

  useConfigureJaws(dispatch, useWebSockets);
  return (
    <OMSContext
      config={componentsContext}
      includeGlobalStyles={false}
      includeReset={false}
      theme={theme}
      jaws={jawsContext}
    >
      <QueryClientProvider client={queryClient}>
        <Suspense fallback={null}>
          <Routes />
        </Suspense>
      </QueryClientProvider>
    </OMSContext>
  );
};

const App = () => {
  return (
    <Provider store={store}>
      <JawsContext />
    </Provider>
  );
};

ReactDOM.render(<App />, elem);
